import React, { useState, useEffect, useMemo } from "react";
import api from "../lib/api";
import { AVAILABILITY_OPTIONS, LANGUAGE_OPTIONS, SUBJECTS_TREE } from "../lib/constants";

export default function FiltersModal({ open, onClose, onApply, initialFilters, accent = "blue", showSubjects = false }) {
  const [locations, setLocations] = useState({});
  const [filters, setFilters] = useState(initialFilters || defaultFilters());
  const [openSubjectCat, setOpenSubjectCat] = useState(null);

  useEffect(() => {
    if (open && Object.keys(locations).length === 0) {
      api.get("/locations").then(({ data }) => setLocations(data.locations || {}));
    }
  }, [open, locations]);

  useEffect(() => {
    if (open) setFilters(initialFilters || defaultFilters());
  }, [open, initialFilters]);

  const comunidades = useMemo(() => Object.keys(locations).sort(), [locations]);
  const ciudades = filters.comunidad ? Object.keys(locations[filters.comunidad] || {}) : [];
  const zonasOptions = filters.comunidad && filters.ciudad ? (locations[filters.comunidad]?.[filters.ciudad] || []) : [];

  const toggleArr = (key, val) => {
    setFilters((f) => {
      const arr = new Set(f[key] || []);
      if (arr.has(val)) arr.delete(val);
      else arr.add(val);
      return { ...f, [key]: Array.from(arr) };
    });
  };

  const toggleSubject = (cat, item) => {
    setFilters((f) => {
      const cur = { ...(f.subjects || {}) };
      const list = new Set(cur[cat] || []);
      if (list.has(item)) list.delete(item);
      else list.add(item);
      if (list.size === 0) delete cur[cat];
      else cur[cat] = Array.from(list);
      return { ...f, subjects: cur };
    });
  };

  const clear = () => setFilters(defaultFilters());

  const apply = () => {
    onApply(filters);
    onClose();
  };

  if (!open) return null;

  const accentColor = accent === "teal" ? "teal" : "blue";
  const pillActive = accent === "teal" ? "kibli-pill-active-teal" : "kibli-pill-active";
  const sliderClass = accent === "teal" ? "kibli-slider teal" : "kibli-slider";
  const sliderVal = ((filters.max_price - 5) / (50 - 5)) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white w-full md:max-w-2xl md:rounded-3xl rounded-t-3xl max-h-[92vh] flex flex-col animate-slide-up overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="filters-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-2xl font-extrabold">Filtros</h2>
          <button onClick={onClose} data-testid="close-filters-btn" className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-slate-900">
            <i className="fa-solid fa-xmark text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Disponibilidad */}
          <section>
            <h3 className="font-bold mb-3">Disponibilidad</h3>
            <div className="flex flex-wrap gap-2">
              {AVAILABILITY_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggleArr("availability", o.value)}
                  data-testid={`availability-${o.value}`}
                  className={`kibli-pill ${filters.availability.includes(o.value) ? pillActive : ""}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </section>

          {/* Ubicación */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold flex items-center gap-2">
                <span>📍</span>Ubicación
              </h3>
              <span className="text-xs text-slate-500">Comunidad → Ciudad → Zonas</span>
            </div>
            <div className="text-sm font-semibold text-slate-700 mb-2">Comunidad Autónoma</div>
            <div className="flex flex-wrap gap-2 mb-4">
              {comunidades.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setFilters((f) => ({ ...f, comunidad: f.comunidad === c ? "" : c, ciudad: "", zonas: [] }))}
                  data-testid={`comunidad-${c}`}
                  className={`kibli-pill ${filters.comunidad === c ? pillActive : ""}`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="text-sm font-semibold text-slate-700 mb-2">
              Ciudad {!filters.comunidad && <span className="text-slate-400 font-normal">(elige una comunidad primero)</span>}
            </div>
            {filters.comunidad ? (
              <div className="flex flex-wrap gap-2 mb-4">
                {ciudades.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setFilters((f) => ({ ...f, ciudad: f.ciudad === c ? "" : c, zonas: [] }))}
                    data-testid={`ciudad-${c}`}
                    className={`kibli-pill ${filters.ciudad === c ? pillActive : ""}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-4 py-3 mb-4 rounded-xl border border-dashed border-slate-300 text-center text-slate-400 text-sm">Bloqueado</div>
            )}

            <div className="text-sm font-semibold text-slate-700 mb-2">
              Zonas / Barrios <span className="text-slate-400 font-normal">(opcional, multi-selección)</span>
            </div>
            {filters.ciudad ? (
              <div className="flex flex-wrap gap-2">
                {zonasOptions.map((z) => (
                  <button
                    key={z}
                    type="button"
                    onClick={() => toggleArr("zonas", z)}
                    data-testid={`zona-${z}`}
                    className={`kibli-pill ${filters.zonas.includes(z) ? pillActive : ""}`}
                  >
                    {z}
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-4 py-3 rounded-xl border border-dashed border-slate-300 text-center text-slate-400 text-sm">Bloqueado</div>
            )}
          </section>

          {/* Precio */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold">Precio máximo</h3>
              <span className={`font-bold ${accent === "teal" ? "text-teal-600" : "text-blue-600"}`} data-testid="max-price-display">
                {filters.max_price}€/h
              </span>
            </div>
            <input
              type="range"
              min="5" max="50" step="1"
              value={filters.max_price}
              onChange={(e) => setFilters((f) => ({ ...f, max_price: Number(e.target.value) }))}
              data-testid="max-price-slider"
              className={`w-full ${sliderClass}`}
              style={{ "--val": `${sliderVal}%` }}
            />
          </section>

          {/* Idioma */}
          <section>
            <h3 className="font-bold mb-3">Idioma</h3>
            <div className="flex flex-wrap gap-2">
              {LANGUAGE_OPTIONS.map((l) => (
                <button
                  key={l}
                  type="button"
                  onClick={() => toggleArr("languages", l)}
                  data-testid={`language-${l}`}
                  className={`kibli-pill ${filters.languages.includes(l) ? pillActive : ""}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </section>

          {/* Asignaturas (only for profesores) */}
          {showSubjects && (
            <section>
              <h3 className="font-bold mb-3">Asignaturas</h3>
              <div className="space-y-2">
                {Object.keys(SUBJECTS_TREE).map((cat) => {
                  const selectedInCat = (filters.subjects?.[cat] || []).length;
                  const isOpen = openSubjectCat === cat;
                  return (
                    <div key={cat} className={`rounded-xl border ${selectedInCat > 0 ? "border-teal-500 bg-teal-50/50" : "border-slate-200 bg-white"}`}>
                      <button
                        type="button"
                        onClick={() => setOpenSubjectCat(isOpen ? null : cat)}
                        data-testid={`subject-cat-${cat}`}
                        className={`w-full flex items-center justify-between px-4 py-3 font-semibold ${selectedInCat > 0 ? "text-teal-700" : "text-slate-700"}`}
                      >
                        <span>{cat}{selectedInCat > 0 && <span className="ml-2 text-xs font-normal">({selectedInCat})</span>}</span>
                        <i className={`fa-solid ${isOpen ? "fa-chevron-up" : "fa-chevron-down"}`}></i>
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-3 flex flex-wrap gap-2">
                          {SUBJECTS_TREE[cat].map((s) => {
                            const checked = (filters.subjects?.[cat] || []).includes(s);
                            return (
                              <button
                                key={s}
                                type="button"
                                onClick={() => toggleSubject(cat, s)}
                                data-testid={`subject-${cat}-${s}`}
                                className={`kibli-pill text-xs ${checked ? "kibli-pill-active-teal" : ""}`}
                              >
                                {s}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-slate-100 bg-white">
          <button type="button" onClick={clear} data-testid="clear-filters-btn" className="flex-1 px-6 py-3 rounded-full font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50">
            Limpiar
          </button>
          <button
            type="button"
            onClick={apply}
            data-testid="apply-filters-btn"
            className={`flex-[2] inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-white font-bold ${accent === "teal" ? "bg-teal-500 hover:bg-teal-600" : "bg-blue-600 hover:bg-blue-700"}`}
          >
            <i className="fa-solid fa-magnifying-glass"></i> Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}

export function defaultFilters() {
  return {
    availability: [],
    comunidad: "",
    ciudad: "",
    zonas: [],
    max_price: 50,
    languages: [],
    subjects: {},
  };
}
